import React, { createContext, useContext, useEffect, useRef, useState } from 'react'
import { message } from 'antd'
import { api, ApiError } from '@/services/api'
import { authStore } from '@/context/auth'

// id → JSON snapshot, used to diff master lists and push only the changes.
const recordMap = (arr: any[]): Map<string, string> =>
  new Map((arr || []).map((r: any) => [r.id, JSON.stringify(r)]))

/* ── Exported types ─────────────────────────────────────────── */

// One physical installation belonging to a generator company.
// A single generator company can operate multiple plants.
export interface GeneratorPlant {
  id: string                  // PL001, PL002 ...
  plantName: string           // Plant Name
  typeOfPower: string[]
  modeOfSupply: string[]
  htServiceNumber: string     // Plant HT No.
  installedCapacityKw: number // Installed Capacity (kW)
  plantAddress: string        // Plant Location
  substationName: string      // Plant Substation
  substationVoltage: string   // kV
  cod: string                 // Commissioning / COD date (DD/MM/YYYY)
  capacity: number            // Proposed Annual Generation, kWh/year
  injectingVoltage: string    // kV
  edc: string
  meterNumber: string
  meterMake: string
  ctRatio: string
  ptRatio: string
  plantLatitude: string
  plantLongitude: string
  synchronizationDate: string
  status: 'active' | 'inactive'
}

export interface Generator {
  id: string
  code: string

  // ── Section 1: Communication Details ──────────────────────────
  name: string                    // Generator Company Name
  contactPersonName: string       // Generator Contact Person Name
  designation: string
  contactMobile: string
  contactEmail: string
  companyAddress: string          // Generator Company Address
  gstin: string                   // Generator GST Number
  panNumber: string
  cinNumber: string               // CIN Number (If Applicable)

  // ── Plants (one generator → multiple plants) ───────────────────
  plants: GeneratorPlant[]

  // ── Top-level summary fields (derived from first/all plants; kept for
  //    backward-compat with Mapping, Dashboard, MeterReadings) ────
  typeOfPower: string[]
  modeOfSupply: string[]
  plantType: string
  capacity: number                // Sum of plants' Proposed Annual Generation, kWh/year
  installedCapacityKw: number     // Sum of plants' Installed Capacity, kW
  htServiceNumber: string
  substationName: string

  // ── Operational fields ─────────────────────────────────────────
  state: string
  tdsRate: number                 // %
  paymentTerms: number            // days
  bankDetails: string
  status: 'active' | 'inactive'
}

// One HT service connection belonging to a consumer company.
// A single consumer can have multiple HT service numbers.
export interface ConsumerService {
  id: string                      // SRV001, SRV002 …
  plantName: string               // Plant / Service Name
  htscNumber: string              // Consumer HT Service Number
  plantAddress: string            // Consumer Plant Address
  typeOfPower: string[]
  typeOfPowerOther?: string
  modeOfSupply: string[]
  modeOfSupplyOther?: string
  sanctionedDemand: number        // kVA / MVA
  drawlVoltage: string            // kV
  substationName: string
  substationVoltage: string       // kV
  edc: string
  powerTariffCategory: string
  existingPowerSupplier: string   // Existing Power Supplier
  avgAnnualConsumption: number    // Avg Annual Consumption, kWh
  avgMonthlyBill: number          // ₹
  plantLatitude: string
  plantLongitude: string
  status: 'active' | 'inactive'
}

export interface Consumer {
  id: string
  code: string

  // ── Section 1: Communication Details ──────────────────────────
  name: string                    // Consumer Company Name
  contactPersonName: string
  designation: string
  contactMobile: string
  contactEmail: string
  companyAddress: string
  gstin: string
  panNumber: string
  cinNumber: string

  // ── HT Services (one consumer → multiple HT connections) ──────
  services: ConsumerService[]

  // ── Discussion history (synced with Daily Call Reports) ───────
  discussionHistory?: import('@/types').Discussion[]

  // ── Top-level summary fields (derived from first/all services;
  //    kept for backward-compat with Mapping, Dashboard) ─────────
  typeOfPower: string[]
  modeOfSupply: string[]
  htscNumber: string
  sanctionedDemand: number
  powerTariffCategory: string

  // ── Operational ───────────────────────────────────────────────
  state: string
  tariffCode: string
  tdsRate: number
  paymentTerms: number
  bankDetails: string
  status: 'active' | 'suspended' | 'inactive'
}

// Shakthi Power tariff — the rate at which Shakthi Power sells power
export interface Tariff {
  id: string
  tariffCode: string
  description: string
  ratePerUnit: number     // ₹/kWh
  powerFacilitationCharge?: number // ₹/unit
  applicableTo: 'generator' | 'consumer' | 'both'
  generatorId?: string    // optional — tariff tied to a specific generator
  consumerId?: string     // optional — tariff tied to a specific consumer
  effectiveFrom: string
  effectiveTo: string
  status: 'active' | 'superseded'
}

// Standalone Daily Call Report record
export interface DcrRecord {
  id: string
  date: string             // YYYY-MM-DD
  employeeName: string
  targetType: 'lead' | 'customer'
  targetId: string
  targetName: string
  contactType: string      // WhatsApp | Phone Call | ...
  customerStatus?: string  // Interested | Negotiating | ...
  discussionDetails: string
  nextFollowUpDate?: string
  createdAt: string        // ISO timestamp
}

// TNEB (TANGEDCO) base / grid tariff — the reference rate against which savings are shown.
// Slabs are ordered C1 → C5; each slab carries its own rate and Electricity Tax %.
export interface TnebTariff {
  id: string
  code: string
  category: string          // HT-I / HT-II / LT Commercial …
  description: string
  ratePerUnit: number       // C4 — Normal Hour Rate (₹/kWh)
  peakRate?: number         // C5 — Peak Hour Rate (₹/kWh)
  nightC1Rate?: number      // C1 — Night Hour Slot 1 Rate (₹/kWh)
  nightC2Rate?: number      // C2 — Night Hour Slot 2 Rate (₹/kWh)
  c3Rate?: number           // C3 — Rate (₹/kWh)
  nightRate?: number        // Compat alias = nightC1Rate (used by SavingsCalculator)
  averageRate?: number      // Average of C1+C2+C3+C4+C5 (₹/kWh)
  c1Tax?: number            // C1 Electricity Tax (%)
  c2Tax?: number            // C2 Electricity Tax (%)
  c3Tax?: number            // C3 Electricity Tax (%)
  c4Tax?: number            // C4 Electricity Tax (%)
  c5Tax?: number            // C5 Electricity Tax (%)
  electricityTax?: number   // Compat alias = c4Tax (used by SavingsCalculator)
  effectiveFrom: string
  status: 'active' | 'superseded'
}

export interface TnebCategory {
  id: string
  name: string            // e.g. "HT1", "HT2", "LT"
  description?: string
  status: 'active' | 'inactive'
}

export interface InjectingVoltage {
  id: string
  value: string           // e.g. "11", "33", "110"
  unit: string            // "kV"
  description?: string
  status: 'active' | 'inactive'
}

export interface DrawalVoltage {
  id: string
  value: string           // e.g. "11", "33", "110"
  unit: string            // "kV"
  description?: string
  status: 'active' | 'inactive'
}

export interface Edc {
  id: string
  name: string            // EDC Name
  place: string           // Place / District
  status: 'active' | 'inactive'
}

// Company bank account — feeds the "Bank Details" block on the Invoice PDF.
// Multiple accounts can be kept on file; the one with isDefault=true (first
// active one if none is marked) is what invoices show.
export interface BankAccount {
  id: string
  bankName: string           // e.g. HDFC Bank Ltd
  branchName: string         // e.g. Chitlapakkam
  accountHolderName?: string // name the account is held in
  accountNumber: string
  ifscCode?: string
  swiftCode?: string
  isDefault: boolean         // used on invoices when true
  status: 'active' | 'inactive'
}

export interface AgreementType {
  id: string
  code: string
  name: string            // e.g. Power Purchase Agreement
  description?: string
  status: 'active' | 'inactive'
}

export interface Slot {
  id: string
  slotCode: string        // C1, C2, C3 …
  startTime: string       // HH:mm
  endTime: string         // HH:mm
  startTime2?: string     // HH:mm (optional second block)
  endTime2?: string       // HH:mm (optional second block)
  category: 'off-peak' | 'normal' | 'peak'
  description: string
  effectiveFrom?: string  // YYYY-MM-DD
  effectiveTo?: string    // YYYY-MM-DD (blank = current)
  status: 'active' | 'inactive'
}

export interface GSTConfig {
  id: string
  slabName: string
  cgstRate: number
  sgstRate: number
  igstRate: number
  hsnCode: string
  sacCode: string
  applicableFor: string
  description: string
  status: 'active' | 'inactive'
}

export interface ModulePermissions {
  view: boolean
  add: boolean
  edit: boolean
  delete: boolean
  email: boolean
  download: boolean
  approve: boolean
  reject: boolean
}

export interface WorkflowStep {
  stepNo: number            // 1, 2, 3 …
  stepName: string          // e.g. "Sales Manager Approval"
  approverName: string      // Employee name
  isRequired: boolean       // mandatory or optional
  action: 'approve' | 'reject' | 'both'  // what action this step allows
}

/**
 * One approval chain per business module — the Workflow Master.
 *
 * This used to live inside each Role row as `Role.workflowConfigs`, but it was
 * never genuinely per-role: consumers read it off the Admin role and fell back
 * to "any active role that happens to have one", i.e. it was already a single
 * company-wide setting kept in an arbitrary place. It is now its own master
 * (table `workflows`, route /master/workflow).
 */
export interface WorkflowConfig {
  id: string
  module: string            // Proposal | Agreement | Invoice | Allocation | Payment | Payout | Mapping
  description?: string
  steps: WorkflowStep[]
  isActive: boolean
  status: 'active' | 'inactive'
}

export interface Role {
  id: string
  name: string
  description: string
  permissions: Record<string, ModulePermissions>
  /** @deprecated Moved to the Workflow Master (see WorkflowConfig). Kept so
   *  legacy rows still round-trip until the column is dropped; nothing reads it. */
  workflowConfigs?: unknown
  status: 'active' | 'inactive'
}

// Employee Master — drives the "Assign To" dropdown in Leads
export interface Employee {
  id: string
  code: string
  // Basic Official Details
  name: string
  department?: string
  role: string
  phone: string
  email: string
  region: string
  photoUrl?: string        // profile photo stored in object storage (R2)

  // Personal Details
  dateOfBirth?: string
  personalAddress?: string
  personalMobile?: string
  altContactNumber?: string
  bloodGroup?: string

  // Commercial / Statutory Details
  aadhaarNumber?: string
  aadhaarCardUrl?: string
  panNumber?: string
  panCardUrl?: string
  esiNumber?: string
  uanNumber?: string
  healthInsuranceNumber?: string
  healthInsuranceProvider?: string
  termInsuranceNumber?: string
  termInsuranceProvider?: string

  // Bank Details
  bankName?: string
  accountNumber?: string
  ifscCode?: string
  branchName?: string

  // Employment Details
  dateOfJoining?: string
  dateOfConfirmation?: string

  status: 'active' | 'inactive'

  // Which applications this employee can log into — Software Access tab.
  softwareAccess?: ('SHAKTHI_POWER' | 'GRAND_SHAKTHI_POWER')[]
  // This employee's role in Grand Shakthi Power's own Role Master (a
  // separate database) — only meaningful when softwareAccess includes
  // GRAND_SHAKTHI_POWER. A free-text role name, not a fixed enum here.
  gspRole?: string
}

// Proposal Template Master
export interface ProposalTemplate {
  id: string
  name: string                        // e.g. "Shakthi Power – Wind & Solar"
  description: string                 // short description shown in dropdown
  isDefault: boolean                  // first active template auto-selected
  status: 'active' | 'inactive'

  // ── Default form values ────────────────────────────────────
  contractPeriod: number              // years
  lockInPeriod: number                // years
  noticePeriod: number                // months
  commencementMonth: string           // e.g. "July 2026"
  validity: 15 | 30                   // days
  commercialTerms: string             // payment terms label
  powerFacilitationCharge: number     // ₹/unit
  gstOnFacilitationCharge: number     // %
  minimumAssuredSupply: string        // e.g. "80 to 85%"
  rateRevision: string                // e.g. "50-50% basis"
  billRaiseMethod: string
  paymentDueInDays: number

  // ── PDF Terms & Conditions (editable clauses) ──────────────
  introParagraph: string              // company intro text in PDF
  billingClause: string               // A) Billing clause
  paymentClause: string               // B) Payment clause
  paymentSecurityClause: string       // C) Payment Security
  rateRevisionClause: string          // D) Rate Revision clause
  minSupplyClause: string             // E) Minimum assured supply
  requiredDocuments: string[]         // F) Required docs list
  signingDocuments: string[]          // G) Documents to sign
  energyAdjustmentClauses: string[]   // H) Energy adjustments bullets
  validityNote: string                // Validity box text
  closingNote: string                 // Closing paragraph

  // ── Free-form rich-text body (CKEditor) ────────────────────
  // Managers can type the entire proposal body here with formatting.
  // When empty, a default body is generated from the structured clauses above.
  // Supports placeholders: {{PRICING_TABLE}}, {{validityDate}}, {{customerName}},
  // {{customerCity}}, {{contractPeriod}}, {{lockInPeriod}}, {{noticePeriod}}, {{deliveredTariff}}.
  bodyHtml?: string

  // ── Cover Page (dynamic — page 1 of the generated PDF) ─────
  coverPageEnabled?: boolean          // include a cover page before the letter
  coverTitle?: string                 // big title, e.g. "Commercial Proposal"
  coverSubtitle?: string              // tagline shown under the title
  coverDescription?: string           // paragraph shown under the subtitle
  coverImage?: string                 // base64 background image for the cover

  // ── Company Content (page 2 — rich text, supports images) ──
  // Free-form "About Us" / company profile page shown right after the cover
  // page and before the proposal letter. Managers edit this with CKEditor,
  // including inline images. Left empty → the page is skipped entirely.
  companyContentHtml?: string

  // Terms & Conditions (page — rich text, CKEditor, like companyContentHtml).
  // If absent, a default is generated from the structured clause fields above.
  termsConditionsHtml?: string
}

// Email Template Master — reusable email templates (follow-up, proposal share,
// payment reminder, …). Managers pick one when sending an email; the subject
// and body support {{placeholders}} that are filled in at send time.
export interface EmailTemplate {
  id: string                          // ET001, ET002 …
  name: string                        // e.g. "Proposal Follow-up"
  category: string                    // Follow Up | Proposal | Invoice | Payment Reminder | Welcome | General
  description: string                 // short description shown in the list / dropdown
  subject: string                     // email subject line (supports {{placeholders}})
  bodyHtml: string                    // rich-text email body (supports {{placeholders}})
  isDefault: boolean                  // default template for its category
  status: 'active' | 'inactive'
}

// One button on a WhatsApp template (quick reply or call-to-action) — mirrors
// the WhatsApp Business API's button shape.
export interface WhatsAppTemplateButton {
  type: 'QUICK_REPLY' | 'URL' | 'PHONE_NUMBER'
  label: string
  value?: string                      // URL or phone number (not used for QUICK_REPLY)
}

// WhatsApp Template Master — reusable WhatsApp message templates (follow-up,
// proposal share, payment reminder, …), shaped after the WhatsApp Business
// API's template structure (header/body/footer/buttons) so it doubles as the
// source of truth once a real BSP (Meta Cloud API / Gupshup / Interakt / …)
// is wired up. Managers pick one when messaging a lead/consumer; body/header
// support {{placeholders}} filled in at send time.
export interface WhatsAppTemplate {
  id: string                          // WT001, WT002 …
  name: string                        // e.g. "Proposal Follow-up"
  category: string                    // Follow Up | Proposal | Invoice | Payment Reminder | Welcome | General
  whatsappCategory: 'Marketing' | 'Utility' | 'Authentication'
  language: string                    // e.g. English, Tamil, Hindi
  description: string                 // short description shown in the list / dropdown
  headerText?: string                 // optional one-line header (supports {{placeholders}})
  bodyText: string                    // main message text (supports {{placeholders}})
  footerText?: string                 // optional small footer text (no placeholders)
  buttons?: WhatsAppTemplateButton[]  // up to 3 quick-reply / call-to-action buttons
  isDefault: boolean                  // default template for its category
  approvalStatus: 'Draft' | 'Pending' | 'Approved' | 'Rejected'
  status: 'active' | 'inactive'
}



// Code Master — one auto-numbering series per master module (prefix +
// zero-padded running number + optional suffix). New records in that
// module get their code atomically generated server-side from this rule
// (see src/utils/codeGenerator.ts) instead of being typed or randomly
// generated client-side.
export interface CodeMaster {
  id: string                          // CM_USERS, CM_GENERATORS, … (fixed, one per module)
  moduleKey: string                   // internal table/resource key this series drives, e.g. "users"
  moduleLabel: string                 // display name, e.g. "Employee"
  prefix: string                      // e.g. "EMP"
  padding: number                     // zero-pad width, e.g. 3 → 001
  nextNumber: number                  // the next number this series will hand out
  suffix?: string
  status: 'active' | 'inactive'
}

/* ── Context ────────────────────────────────────────────────── */
export interface PowerType {
  id: string
  code: string
  name: string
  description?: string
  requiredUnits?: number   // Required units per annum (kWh/year)
  status: 'active' | 'inactive'
}

export interface SupplyMode {
  id: string
  code: string
  name: string
  description?: string
  requiredUnits?: number   // Required units per annum (kWh/year)
  status: 'active' | 'inactive'
}


interface MasterDataContextType {
  dcrRecords:         DcrRecord[];         setDcrRecords:         React.Dispatch<React.SetStateAction<DcrRecord[]>>
  generators:         Generator[];         setGenerators:         React.Dispatch<React.SetStateAction<Generator[]>>
  consumers:          Consumer[];          setConsumers:          React.Dispatch<React.SetStateAction<Consumer[]>>
  tariffs:            Tariff[];            setTariffs:            React.Dispatch<React.SetStateAction<Tariff[]>>
  tnebTariffs:        TnebTariff[];        setTnebTariffs:        React.Dispatch<React.SetStateAction<TnebTariff[]>>
  tnebCategories:     TnebCategory[];      setTnebCategories:     React.Dispatch<React.SetStateAction<TnebCategory[]>>
  slots:              Slot[];              setSlots:              React.Dispatch<React.SetStateAction<Slot[]>>
  gstConfigs:         GSTConfig[];         setGstConfigs:         React.Dispatch<React.SetStateAction<GSTConfig[]>>
  proposalTemplates:  ProposalTemplate[];  setProposalTemplates:  React.Dispatch<React.SetStateAction<ProposalTemplate[]>>
  emailTemplates:      EmailTemplate[];      setEmailTemplates:      React.Dispatch<React.SetStateAction<EmailTemplate[]>>
  whatsappTemplates:   WhatsAppTemplate[];   setWhatsappTemplates:   React.Dispatch<React.SetStateAction<WhatsAppTemplate[]>>
  codeMasters:         CodeMaster[];         setCodeMasters:         React.Dispatch<React.SetStateAction<CodeMaster[]>>
  bankAccounts:        BankAccount[];        setBankAccounts:        React.Dispatch<React.SetStateAction<BankAccount[]>>

  employees:           Employee[];           setEmployees:          React.Dispatch<React.SetStateAction<Employee[]>>
  roles:              Role[];              setRoles:              React.Dispatch<React.SetStateAction<Role[]>>
  workflows:          WorkflowConfig[];    setWorkflows:          React.Dispatch<React.SetStateAction<WorkflowConfig[]>>
  powerTypes:         PowerType[];         setPowerTypes:         React.Dispatch<React.SetStateAction<PowerType[]>>
  supplyModes:        SupplyMode[];        setSupplyModes:        React.Dispatch<React.SetStateAction<SupplyMode[]>>
  edcs:               Edc[];               setEdcs:               React.Dispatch<React.SetStateAction<Edc[]>>
  agreementTypes:     AgreementType[];     setAgreementTypes:     React.Dispatch<React.SetStateAction<AgreementType[]>>
  injectingVoltages:  InjectingVoltage[];  setInjectingVoltages:  React.Dispatch<React.SetStateAction<InjectingVoltage[]>>
  drawalVoltages:     DrawalVoltage[];     setDrawalVoltages:     React.Dispatch<React.SetStateAction<DrawalVoltage[]>>
  /** Re-fetch every master list from the API. Call right after login — reads
   *  now require a valid token, so the very first hydrate (which runs before
   *  the user has signed in) always comes back empty; this is what actually
   *  populates the app once a session exists. */
  refreshMasterData:  () => Promise<void>

  /** True once the first hydrateAll() pass has finished (success or failure of
   *  individual calls — it never blocks forever on a slow/offline backend).
   *  False for the brief window right after mount/login while every list
   *  above is still its empty initial state. Pages that show totals/tables
   *  computed from this data should key a loading spinner off this instead
   *  of rendering "0" for real content while the first fetch is in flight. */
  masterDataLoaded: boolean

  /** Explicit deletes — call the API immediately and only drop the row from
   *  local state once the server confirms. Deletes are NEVER inferred from
   *  the write-through sync's array diffing (see below): a browser's local
   *  copy of a list can be incomplete or stale for reasons that have nothing
   *  to do with a row being deleted (a hydrate still in flight, one tab not
   *  yet knowing about another tab's add, a cold-starting backend, …), so
   *  treating "missing from my local array" as "the user deleted this" is
   *  unsafe the moment more than one session exists. Throws on failure (a
   *  message.error is already shown) so callers can skip their own success
   *  toast/navigation. */
  deleteDcrRecord:        (id: string) => Promise<void>
  deleteGenerator:        (id: string) => Promise<void>
  deleteConsumer:          (id: string) => Promise<void>
  deleteTariff:            (id: string) => Promise<void>
  deleteTnebTariff:        (id: string) => Promise<void>
  deleteTnebCategory:      (id: string) => Promise<void>
  deleteSlot:              (id: string) => Promise<void>
  deleteGstConfig:         (id: string) => Promise<void>
  deleteProposalTemplate:  (id: string) => Promise<void>
  deleteEmailTemplate:     (id: string) => Promise<void>
  deleteWhatsappTemplate:  (id: string) => Promise<void>
  deleteCodeMaster:        (id: string) => Promise<void>
  deleteBankAccount:       (id: string) => Promise<void>
  deleteEmployee:          (id: string) => Promise<void>
  deleteRole:              (id: string) => Promise<void>
  deleteWorkflow:          (id: string) => Promise<void>
  deletePowerType:         (id: string) => Promise<void>
  deleteSupplyMode:        (id: string) => Promise<void>
  deleteEdc:               (id: string) => Promise<void>
  deleteAgreementType:     (id: string) => Promise<void>
  deleteInjectingVoltage:  (id: string) => Promise<void>
  deleteDrawalVoltage:     (id: string) => Promise<void>

  /** Explicit create/update — calls the API immediately and only reflects the
   *  server's saved row in local state once the write actually confirms.
   *  Master page onFinish handlers used to update local state and show a
   *  "success" message *before* the real POST/PUT (fired later by the
   *  write-through sync) had a chance to complete or fail — so a rejected
   *  write (e.g. a duplicate-email 409) still showed "success", and the
   *  phantom local row only vanished on the next reload. isEdit picks
   *  POST vs PUT; throws on failure (message.error already shown) so
   *  callers can leave the form open instead of navigating away. */
  saveDcrRecord:        (payload: any, isEdit: boolean) => Promise<DcrRecord>
  saveGenerator:        (payload: any, isEdit: boolean) => Promise<Generator>
  saveConsumer:          (payload: any, isEdit: boolean) => Promise<Consumer>
  saveTariff:            (payload: any, isEdit: boolean) => Promise<Tariff>
  saveTnebTariff:        (payload: any, isEdit: boolean) => Promise<TnebTariff>
  saveTnebCategory:      (payload: any, isEdit: boolean) => Promise<TnebCategory>
  saveSlot:              (payload: any, isEdit: boolean) => Promise<Slot>
  saveGstConfig:         (payload: any, isEdit: boolean) => Promise<GSTConfig>
  saveProposalTemplate:  (payload: any, isEdit: boolean) => Promise<ProposalTemplate>
  saveEmailTemplate:     (payload: any, isEdit: boolean) => Promise<EmailTemplate>
  saveWhatsappTemplate:  (payload: any, isEdit: boolean) => Promise<WhatsAppTemplate>
  saveCodeMaster:        (payload: any, isEdit: boolean) => Promise<CodeMaster>
  saveBankAccount:       (payload: any, isEdit: boolean) => Promise<BankAccount>
  saveEmployee:          (payload: any, isEdit: boolean) => Promise<Employee>
  saveRole:              (payload: any, isEdit: boolean) => Promise<Role>
  saveWorkflow:          (payload: any, isEdit: boolean) => Promise<WorkflowConfig>
  savePowerType:         (payload: any, isEdit: boolean) => Promise<PowerType>
  saveSupplyMode:        (payload: any, isEdit: boolean) => Promise<SupplyMode>
  saveEdc:               (payload: any, isEdit: boolean) => Promise<Edc>
  saveAgreementType:     (payload: any, isEdit: boolean) => Promise<AgreementType>
  saveInjectingVoltage:  (payload: any, isEdit: boolean) => Promise<InjectingVoltage>
  saveDrawalVoltage:     (payload: any, isEdit: boolean) => Promise<DrawalVoltage>
}

const MasterDataContext = createContext<MasterDataContextType | null>(null)

export const MasterDataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Start empty — the backend (MySQL) is the source of truth, loaded below.
  const [dcrRecords,        setDcrRecords]        = useState<DcrRecord[]>([])
  const [generators,        setGenerators]        = useState<Generator[]>([])
  const [consumers,         setConsumers]         = useState<Consumer[]>([])
  const [tariffs,           setTariffs]           = useState<Tariff[]>([])
  const [tnebTariffs,       setTnebTariffs]       = useState<TnebTariff[]>([])
  const [tnebCategories,    setTnebCategories]    = useState<TnebCategory[]>([])
  const [slots,             setSlots]             = useState<Slot[]>([])
  const [gstConfigs,        setGstConfigs]        = useState<GSTConfig[]>([])
  const [proposalTemplates, setProposalTemplates] = useState<ProposalTemplate[]>([])
  const [emailTemplates,    setEmailTemplates]    = useState<EmailTemplate[]>([])
  const [whatsappTemplates, setWhatsappTemplates] = useState<WhatsAppTemplate[]>([])
  const [codeMasters,       setCodeMasters]       = useState<CodeMaster[]>([])
  const [bankAccounts,      setBankAccounts]      = useState<BankAccount[]>([])

  const [employees,         setEmployees]         = useState<Employee[]>([])
  const [roles,             setRoles]             = useState<Role[]>([])
  const [workflows,         setWorkflows]         = useState<WorkflowConfig[]>([])
  const [powerTypes,        setPowerTypes]        = useState<PowerType[]>([])
  const [supplyModes,       setSupplyModes]       = useState<SupplyMode[]>([])
  const [edcs,              setEdcs]              = useState<Edc[]>([])
  const [agreementTypes,    setAgreementTypes]    = useState<AgreementType[]>([])
  const [injectingVoltages, setInjectingVoltages] = useState<InjectingVoltage[]>([])
  const [drawalVoltages,    setDrawalVoltages]    = useState<DrawalVoltage[]>([])

  // Tracks what the backend currently has for each write-through list, so the
  // sync effect below only POSTs/PUTs/DELETEs the rows that actually changed.
  const syncedRef = useRef<Record<string, Map<string, string>> | null>(null)
  if (!syncedRef.current) {
    syncedRef.current = {
      dcrRecords:  new Map(),
      generators:  new Map(),
      consumers:   new Map(),
      tariffs:     new Map(),
      tnebTariffs: new Map(),
      tnebCategories: new Map(),
      slots:       new Map(),
      gstConfigs:  new Map(),
      powerTypes:  new Map(),
      supplyModes: new Map(),
      edcs:           new Map(),
      agreementTypes: new Map(),
      injectingVoltages: new Map(),
      drawalVoltages: new Map(),
      employees:   new Map(),
      roles:       new Map(),
      workflows:   new Map(),
      proposalTemplates: new Map(),
      emailTemplates: new Map(),
      whatsappTemplates: new Map(),
      codeMasters: new Map(),
      bankAccounts: new Map(),

    }
  }

  // SAFETY GATE: The write-through sync must NEVER fire before at least one
  // successful hydrate has completed. Without this guard, if the backend is
  // slow / cold-starting (Railway), the hydrate times out, all state arrays
  // stay empty, but syncedRef still holds the previous snapshot from an
  // earlier session — the sync would then see every row as "deleted" and
  // issue DELETE requests for all of them, wiping the database.
  const hydratedOnceRef = useRef<boolean>(false)
  // Same "first hydrate finished" moment as hydratedOnceRef, but as state
  // (not a ref) so components can actually re-render off it — see
  // masterDataLoaded in the context type above.
  const [masterDataLoaded, setMasterDataLoaded] = useState(false)

  // Holds the current hydrateAll() so refreshMasterData() (called right after
  // login) can trigger the same fetch the mount effect below performs.
  const hydrateAllRef = useRef<() => Promise<void>>(async () => {})
  const refreshMasterData = () => hydrateAllRef.current()

  // ── Hydrate all master data from the Postgres backend ───────────────────
  // The database is the single source of truth — no seed data, no localStorage.
  // Reads require a valid session token, so this only returns real rows once
  // someone is logged in; see refreshMasterData above for the post-login call.
  useEffect(() => {
    let alive = true
    const hydrate = async (path: string, setter: (rows: any) => void, syncKey?: string) => {
      try {
        const data = await api.get<any[]>(path)
        if (alive && Array.isArray(data)) {
          const snap = syncKey ? syncedRef.current?.[syncKey] : undefined
          const serverIds = new Set(data.map((r: any) => r.id))
          // Merge, don't overwrite: a row added/edited locally while this fetch was
          // in flight (slow/cold-starting backend, or straight after login) isn't in
          // this now-stale server response yet. Overwriting would make it vanish
          // from state, and the write-through sync effect below would then read
          // that as "the user deleted it" and push a DELETE for a row nobody
          // deleted — the "master data keeps getting reset" failure mode.
          setter((current: any[]) => {
            const pending = (current || []).filter((r: any) => !serverIds.has(r.id) && !snap?.has(r.id))
            return pending.length ? [...data, ...pending] : data
          })
          if (syncKey && syncedRef.current) syncedRef.current[syncKey] = recordMap(data)
        }
      } catch (err) {
        console.error(`API Hydrate Error for ${path}:`, err)
        // backend not reachable / not logged in yet — leave the list as-is
        // (do NOT clear it — clearing would cause the sync effect to issue
        // DELETE requests for every row that was previously in syncedRef).
      }
    }

    const hydrateAll = async () => {
      await Promise.all([
        hydrate('/dcr',                 setDcrRecords,        'dcrRecords'),
        hydrate('/generators',         setGenerators,        'generators'),
        hydrate('/consumers',          setConsumers,         'consumers'),
        hydrate('/tariffs',            setTariffs,           'tariffs'),
        hydrate('/tneb-tariffs',       setTnebTariffs,       'tnebTariffs'),
        hydrate('/tneb-categories',    setTnebCategories,    'tnebCategories'),
        hydrate('/slots',              setSlots,             'slots'),
        hydrate('/gst-configs',        setGstConfigs,        'gstConfigs'),
        hydrate('/power-types',        setPowerTypes,        'powerTypes'),
        hydrate('/supply-modes',       setSupplyModes,       'supplyModes'),
        hydrate('/edcs',               setEdcs,              'edcs'),
        hydrate('/agreement-types',    setAgreementTypes,    'agreementTypes'),
        hydrate('/injecting-voltages', setInjectingVoltages, 'injectingVoltages'),
        hydrate('/drawal-voltages',    setDrawalVoltages,    'drawalVoltages'),
        hydrate('/proposal-templates', setProposalTemplates, 'proposalTemplates'),
        hydrate('/email-templates',    setEmailTemplates,    'emailTemplates'),
        hydrate('/whatsapp-templates', setWhatsappTemplates, 'whatsappTemplates'),
        hydrate('/code-masters',       setCodeMasters,       'codeMasters'),
        hydrate('/bank-masters',       setBankAccounts,      'bankAccounts'),

        hydrate('/users',              setEmployees,         'employees'),
        hydrate('/roles',              setRoles,             'roles'),
        hydrate('/workflows',          setWorkflows,         'workflows'),
      ])
      // Mark that at least one hydrate cycle has completed so the write-through
      // sync is allowed to run. This prevents spurious DELETEs when the backend
      // is slow to respond on first mount.
      if (alive) {
        hydratedOnceRef.current = true
        setMasterDataLoaded(true)
      }
    }

    hydrateAllRef.current = hydrateAll
    // Only fire the initial hydrate when a session already exists (e.g. a
    // hard refresh while logged in). Before login there's no token, so this
    // would just 401 on every call — pure waste, and it would also latch
    // masterDataLoaded true off that doomed attempt, hiding pages' loading
    // spinners before the post-login refreshMasterData() (which actually
    // succeeds) has landed. LoginPage.tsx calls refreshMasterData() itself
    // right after a successful login, so that case is still covered.
    if (authStore.isLoggedIn()) hydrateAll()

    return () => { alive = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ── Write-through: persist master creates/updates to Postgres ────────────
  // Diffs each list against the last-known server snapshot and pushes only
  // the changes. Fire-and-forget: failures (offline) are ignored so the UI
  // is never blocked — this is best-effort persistence.
  //
  // Deletes are NOT handled here — see deleteXxx / makeDelete below. They
  // used to be inferred from this same diff ("an id present in the last
  // snapshot but missing from the current array must have been deleted"),
  // but that is unsafe the moment more than one browser session exists: a
  // session's local array can be incomplete for reasons that have nothing to
  // do with a real delete (a hydrate still in flight, this tab never having
  // learned about a row another tab/device created, a cold-starting
  // backend, …). That bug was confirmed live — a role created directly via
  // the API, which no browser's local state ever held, was still wrongly
  // soft-deleted by this inference. Deletes are now only ever fired by an
  // explicit user action (see deleteXxx below), never inferred from a
  // shrinking array.
  //
  // SAFETY RULE: only runs after hydratedOnceRef is true (at least one
  // successful fetch). Without this, a backend cold-start timeout leaves
  // state empty, making the diff try to re-create every row as "new".
  useEffect(() => {
    const snap = syncedRef.current
    // Block sync until hydrate has run at least once successfully.
    if (!snap || !hydratedOnceRef.current) return

    // A row is only folded into `snap[key]` (marked "known synced") once its
    // write actually confirms with the server. Previously this happened
    // synchronously right after firing the (unawaited) API call, so a failed
    // write — e.g. the Railway backend cold-starting after being asleep —
    // was silently swallowed by .catch(console.error) yet still considered
    // "saved". The next reload then hydrated from the real DB (which never
    // got the row) and the record vanished with no indication anything had
    // gone wrong. Now failures are (a) never marked synced, so the next sync
    // pass retries them, and (b) surfaced to the user via message.error.
    const sync = (key: string, resource: string, arr: any[]) => {
      const prev = snap[key]
      const cur = recordMap(arr)

      for (const [id, json] of cur) {
        if (prev.get(id) === json) continue // unchanged — already marked synced
        const parsed = JSON.parse(json)
        const isNew = !prev.has(id)
        const req = isNew ? api.post(resource, parsed) : api.put(`${resource}/${id}`, parsed)
        void req
          .then(() => {
            if (snap[key]) snap[key] = new Map(snap[key]).set(id, json)
          })
          .catch(err => {
            console.error(`[sync] Failed to ${isNew ? 'create' : 'update'} ${resource}/${id}:`, err)
            // Same rule as makeSave: a 4xx (duplicate name/code → 409, …)
            // carries the real reason — show it instead of blaming the
            // connection, so a rejected duplicate doesn't read as a network hiccup.
            if (err instanceof ApiError && err.status && err.status < 500 && err.message) {
              message.error(err.message)
            } else {
              message.error("Couldn't save your changes to the server — check your connection and try again.")
            }
          })
      }
    }

    sync('dcrRecords', '/dcr', dcrRecords)
    sync('generators',  '/generators',   generators)
    sync('consumers',   '/consumers',    consumers)
    sync('tariffs',     '/tariffs',      tariffs)
    sync('tnebTariffs', '/tneb-tariffs', tnebTariffs)
    sync('tnebCategories', '/tneb-categories', tnebCategories)
    sync('slots',       '/slots',        slots)
    sync('gstConfigs',  '/gst-configs',  gstConfigs)
    sync('powerTypes',  '/power-types',  powerTypes)
    sync('supplyModes', '/supply-modes', supplyModes)
    sync('edcs',           '/edcs',            edcs)
    sync('agreementTypes', '/agreement-types', agreementTypes)
    sync('injectingVoltages', '/injecting-voltages', injectingVoltages)
    sync('drawalVoltages', '/drawal-voltages', drawalVoltages)
    sync('employees',   '/users',        employees)
    sync('roles',       '/roles',        roles)
    sync('workflows',   '/workflows',    workflows)
    sync('proposalTemplates', '/proposal-templates', proposalTemplates)
    sync('emailTemplates',    '/email-templates',    emailTemplates)
    sync('whatsappTemplates', '/whatsapp-templates', whatsappTemplates)
    sync('codeMasters',       '/code-masters',       codeMasters)
    sync('bankAccounts',      '/bank-masters',       bankAccounts)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dcrRecords, generators, consumers, tariffs, tnebTariffs, tnebCategories, slots, gstConfigs, powerTypes, supplyModes, edcs, injectingVoltages, drawalVoltages, agreementTypes, employees, roles, proposalTemplates, emailTemplates, whatsappTemplates, codeMasters, bankAccounts, workflows])

  // ── Explicit deletes ──────────────────────────────────────────────────────
  // Calls the API immediately and only drops the row from local state (and
  // the sync snapshot) once the server confirms. See the write-through
  // effect above for why deletes are never inferred from array diffing.
  // Admin-account protection (e.g. demo@shakthi.com) is enforced server-side
  // in userService.remove() — a blocked delete 403s, is surfaced via
  // message.error below, and the row is correctly never removed locally.
  const makeDelete = <T extends { id: string }>(
    resource: string,
    syncKey: string,
    setter: React.Dispatch<React.SetStateAction<T[]>>,
  ) => async (id: string): Promise<void> => {
    try {
      await api.del(`${resource}/${id}`)
    } catch (err) {
      console.error(`[delete] Failed to delete ${resource}/${id}:`, err)
      // Same rule as makeSave: a 4xx carries the real reason (e.g. protected
      // admin account → 403) — show it instead of blaming the connection.
      if (err instanceof ApiError && err.status && err.status < 500 && err.message) {
        message.error(err.message)
      } else {
        message.error("Couldn't delete this record on the server — check your connection and try again.")
      }
      throw err
    }
    setter(prev => prev.filter(r => r.id !== id))
    const snap = syncedRef.current?.[syncKey]
    if (snap) {
      const next = new Map(snap)
      next.delete(id)
      syncedRef.current![syncKey] = next
    }
  }

  const deleteDcrRecord       = makeDelete<DcrRecord>('/dcr', 'dcrRecords', setDcrRecords)
  const deleteGenerator       = makeDelete<Generator>('/generators', 'generators', setGenerators)
  const deleteConsumer        = makeDelete<Consumer>('/consumers', 'consumers', setConsumers)
  const deleteTariff          = makeDelete<Tariff>('/tariffs', 'tariffs', setTariffs)
  const deleteTnebTariff      = makeDelete<TnebTariff>('/tneb-tariffs', 'tnebTariffs', setTnebTariffs)
  const deleteTnebCategory    = makeDelete<TnebCategory>('/tneb-categories', 'tnebCategories', setTnebCategories)
  const deleteSlot            = makeDelete<Slot>('/slots', 'slots', setSlots)
  const deleteGstConfig       = makeDelete<GSTConfig>('/gst-configs', 'gstConfigs', setGstConfigs)
  const deleteProposalTemplate = makeDelete<ProposalTemplate>('/proposal-templates', 'proposalTemplates', setProposalTemplates)
  const deleteEmailTemplate   = makeDelete<EmailTemplate>('/email-templates', 'emailTemplates', setEmailTemplates)
  const deleteWhatsappTemplate = makeDelete<WhatsAppTemplate>('/whatsapp-templates', 'whatsappTemplates', setWhatsappTemplates)
  const deleteCodeMaster     = makeDelete<CodeMaster>('/code-masters', 'codeMasters', setCodeMasters)
  const deleteBankAccount    = makeDelete<BankAccount>('/bank-masters', 'bankAccounts', setBankAccounts)
  const deleteEmployee        = makeDelete<Employee>('/users', 'employees', setEmployees)
  const deleteRole            = makeDelete<Role>('/roles', 'roles', setRoles)
  const deleteWorkflow        = makeDelete<WorkflowConfig>('/workflows', 'workflows', setWorkflows)
  const deletePowerType       = makeDelete<PowerType>('/power-types', 'powerTypes', setPowerTypes)
  const deleteSupplyMode      = makeDelete<SupplyMode>('/supply-modes', 'supplyModes', setSupplyModes)
  const deleteEdc             = makeDelete<Edc>('/edcs', 'edcs', setEdcs)
  const deleteAgreementType   = makeDelete<AgreementType>('/agreement-types', 'agreementTypes', setAgreementTypes)
  const deleteInjectingVoltage = makeDelete<InjectingVoltage>('/injecting-voltages', 'injectingVoltages', setInjectingVoltages)
  const deleteDrawalVoltage   = makeDelete<DrawalVoltage>('/drawal-voltages', 'drawalVoltages', setDrawalVoltages)

  // ── Explicit create/update ────────────────────────────────────────────────
  // Calls the API immediately and only reflects the server's saved row in
  // local state (and the sync snapshot) once the write actually confirms.
  // See the interface doc comment above for why this replaced the old
  // "update local state + show success, hope the async write goes through"
  // pattern in every Master page's onFinish.
  const makeSave = <T extends { id: string }>(
    resource: string,
    syncKey: string,
    setter: React.Dispatch<React.SetStateAction<T[]>>,
  ) => async (payload: any, isEdit: boolean): Promise<T> => {
    let saved: T
    try {
      saved = isEdit
        ? await api.put<T>(`${resource}/${payload.id}`, payload)
        : await api.post<T>(resource, payload)
    } catch (err) {
      console.error(`[save] Failed to save ${resource}:`, err)
      // A 4xx is the server refusing the data itself (duplicate name/code/
      // email → 409, permission denied → 403, …) — show its actual message
      // so the user knows what to fix. Anything else reads as connectivity.
      if (err instanceof ApiError && err.status && err.status < 500 && err.message) {
        message.error(err.message)
      } else {
        message.error("Couldn't save your changes to the server — check your connection and try again.")
      }
      throw err
    }
    // New records go to the front, matching the backend's newest-first list()
    // order (crudService.js) — otherwise a just-created row would sit at the
    // bottom of the list until the next full reload.
    setter(prev => isEdit ? prev.map(r => r.id === saved.id ? saved : r) : [saved, ...prev])
    const snap = syncedRef.current?.[syncKey]
    if (snap) {
      syncedRef.current![syncKey] = new Map(snap).set(saved.id, JSON.stringify(saved))
    }
    return saved
  }

  const saveDcrRecord       = makeSave<DcrRecord>('/dcr', 'dcrRecords', setDcrRecords)
  const saveGenerator       = makeSave<Generator>('/generators', 'generators', setGenerators)
  const saveConsumer        = makeSave<Consumer>('/consumers', 'consumers', setConsumers)
  const saveTariff          = makeSave<Tariff>('/tariffs', 'tariffs', setTariffs)
  const saveTnebTariff      = makeSave<TnebTariff>('/tneb-tariffs', 'tnebTariffs', setTnebTariffs)
  const saveTnebCategory    = makeSave<TnebCategory>('/tneb-categories', 'tnebCategories', setTnebCategories)
  const saveSlot            = makeSave<Slot>('/slots', 'slots', setSlots)
  const saveGstConfig       = makeSave<GSTConfig>('/gst-configs', 'gstConfigs', setGstConfigs)
  const saveProposalTemplate = makeSave<ProposalTemplate>('/proposal-templates', 'proposalTemplates', setProposalTemplates)
  const saveEmailTemplate   = makeSave<EmailTemplate>('/email-templates', 'emailTemplates', setEmailTemplates)
  const saveWhatsappTemplate = makeSave<WhatsAppTemplate>('/whatsapp-templates', 'whatsappTemplates', setWhatsappTemplates)
  const saveCodeMaster       = makeSave<CodeMaster>('/code-masters', 'codeMasters', setCodeMasters)
  const saveBankAccount      = makeSave<BankAccount>('/bank-masters', 'bankAccounts', setBankAccounts)
  const saveEmployee        = makeSave<Employee>('/users', 'employees', setEmployees)
  const saveRole            = makeSave<Role>('/roles', 'roles', setRoles)
  const saveWorkflow        = makeSave<WorkflowConfig>('/workflows', 'workflows', setWorkflows)
  const savePowerType       = makeSave<PowerType>('/power-types', 'powerTypes', setPowerTypes)
  const saveSupplyMode      = makeSave<SupplyMode>('/supply-modes', 'supplyModes', setSupplyModes)
  const saveEdc             = makeSave<Edc>('/edcs', 'edcs', setEdcs)
  const saveAgreementType   = makeSave<AgreementType>('/agreement-types', 'agreementTypes', setAgreementTypes)
  const saveInjectingVoltage = makeSave<InjectingVoltage>('/injecting-voltages', 'injectingVoltages', setInjectingVoltages)
  const saveDrawalVoltage   = makeSave<DrawalVoltage>('/drawal-voltages', 'drawalVoltages', setDrawalVoltages)

  return (
    <MasterDataContext.Provider value={{
      dcrRecords, setDcrRecords,
      generators, setGenerators,
      consumers, setConsumers,
      tariffs, setTariffs,
      tnebTariffs, setTnebTariffs,
      tnebCategories, setTnebCategories,
      slots, setSlots,
      gstConfigs, setGstConfigs,
      proposalTemplates, setProposalTemplates,
      emailTemplates, setEmailTemplates,
      whatsappTemplates, setWhatsappTemplates,
      codeMasters, setCodeMasters,
      bankAccounts, setBankAccounts,

      employees, setEmployees,
      roles, setRoles,
      workflows, setWorkflows,
      powerTypes, setPowerTypes,
      supplyModes, setSupplyModes,
      edcs, setEdcs,
      agreementTypes, setAgreementTypes,
      injectingVoltages, setInjectingVoltages,
      drawalVoltages, setDrawalVoltages,
      refreshMasterData,
      masterDataLoaded,

      deleteDcrRecord,
      deleteGenerator,
      deleteConsumer,
      deleteTariff,
      deleteTnebTariff,
      deleteTnebCategory,
      deleteSlot,
      deleteGstConfig,
      deleteProposalTemplate,
      deleteEmailTemplate,
      deleteWhatsappTemplate,
      deleteCodeMaster,
      deleteBankAccount,
      deleteEmployee,
      deleteRole,
      deleteWorkflow,
      deletePowerType,
      deleteSupplyMode,
      deleteEdc,
      deleteAgreementType,
      deleteInjectingVoltage,
      deleteDrawalVoltage,

      saveDcrRecord,
      saveGenerator,
      saveConsumer,
      saveTariff,
      saveTnebTariff,
      saveTnebCategory,
      saveSlot,
      saveGstConfig,
      saveProposalTemplate,
      saveEmailTemplate,
      saveWhatsappTemplate,
      saveCodeMaster,
      saveBankAccount,
      saveEmployee,
      saveRole,
      saveWorkflow,
      savePowerType,
      saveSupplyMode,
      saveEdc,
      saveAgreementType,
      saveInjectingVoltage,
      saveDrawalVoltage,
    }}>
      {children}
    </MasterDataContext.Provider>
  )
}

export const useMasterData = (): MasterDataContextType => {
  const ctx = useContext(MasterDataContext)
  if (!ctx) throw new Error('useMasterData must be used within <MasterDataProvider>')
  return ctx
}
